import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { withAlpha } from '../../theme';

export interface FacebookPageCardData {
  id: string;
  page_id: string;
  page_name: string;
  page_access_token?: string;
  status?: string;
  isVerified?: boolean;
  verifiedBy?: string;
  verifiedName?: string;
  is_default?: boolean;
}

interface FacebookPageCardProps {
  page: FacebookPageCardData;
  onPress?: (page: FacebookPageCardData) => void;
  onImportConversations?: (page: FacebookPageCardData) => void;
  isImporting?: boolean;
  /** Backfills post history from Facebook (Graph API) into the local record — new
   * posts made directly on Facebook after this sync automatically come in via the
   * page's feed webhook; this covers everything older than that. */
  onSyncPosts?: (page: FacebookPageCardData) => void;
  isSyncingPosts?: boolean;
  onSetDefault?: (page: FacebookPageCardData) => void;
  onDelete?: (pageId: string) => void;
  /** Retries webhook subscription for a page stuck 'pending' — distinct from the old manual
   * "Verify" concept, which never called Meta and has been removed. */
  onRegister?: (page: FacebookPageCardData) => void;
  isRegistering?: boolean;
  showActions?: boolean;
  /** True once we've confirmed via Graph API that this page's live webhook
   * subscription doesn't actually include 'feed' — distinct from the page's
   * overall verified/connected status, since a page can be fully verified
   * for messaging while still never having picked up a newer subscribed
   * field added after it first connected (Meta never auto-upgrades an
   * existing subscription; re-registering is required). When true, shows a
   * warning badge and re-enables the register action even for an otherwise
   * "connected" page. */
  feedNotSubscribed?: boolean;
}

export const FacebookPageCard: React.FC<FacebookPageCardProps> = ({
  page,
  onPress,
  onImportConversations,
  isImporting = false,
  onSyncPosts,
  isSyncingPosts = false,
  onSetDefault,
  onDelete,
  onRegister,
  isRegistering = false,
  showActions = true,
  feedNotSubscribed = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const isPageVerified = page.isVerified || page.status === 'verified' || page.status === 'VERIFIED' || page.status === 'connected';
  const isMetaVerified = page.verifiedBy === 'meta_admin';
  const displayName = page.page_name || page.page_id || 'Unknown';
  const statusText = isPageVerified
    ? (isMetaVerified ? 'Verified by Meta' : 'Verified')
    : 'Pending Verification';

  return (
    <TouchableOpacity
      style={[
        styles.card,
        isPageVerified && styles.verifiedCard,
        page.is_default && styles.defaultCard,
      ]}
      onPress={() => onPress?.(page)}
      activeOpacity={0.7}
    >
      <View style={styles.cardContent}>
        <View style={styles.pageInfo}>
          <View style={styles.pageHeader}>
            <View style={[
              styles.pageIconWrapper,
              isPageVerified && styles.verifiedIconWrapper,
            ]}>
              <Ionicons
                name={isPageVerified ? 'checkmark-circle' : 'logo-facebook'}
                size={20}
                color={isPageVerified ? colors.success : '#1877F2'}
              />
            </View>
            <View style={styles.pageDetails}>
              <View style={styles.pageNameRow}>
                <Text style={[styles.pageName, isPageVerified && styles.verifiedText]}>
                  {displayName}
                </Text>
                {page.is_default && (
                  <View style={styles.defaultBadge}>
                    <Text style={styles.defaultBadgeText}>Default</Text>
                  </View>
                )}
              </View>
              <View style={styles.statusRow}>
                <Text style={[
                  styles.statusText,
                  isPageVerified ? styles.verifiedStatusText : styles.pendingStatusText,
                ]}>
                  {statusText}
                </Text>
                {page.verifiedName && (
                  <Text style={styles.verifiedName}>
                    as &quot;{page.verifiedName}&quot;
                  </Text>
                )}
              </View>
              {page.page_id && (
                <Text style={styles.pageDetail}>
                  ID: {page.page_id}
                </Text>
              )}
              {feedNotSubscribed && (
                <View style={styles.feedWarningRow}>
                  <Ionicons name="warning-outline" size={12} color={colors.warning} />
                  <Text style={styles.feedWarningText}>Posts/comments webhook not active — re-register to fix</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {showActions && (
          <View style={styles.actions}>
            {isPageVerified && onImportConversations && (
              <TouchableOpacity
                style={[styles.actionButton, styles.importButton]}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onImportConversations(page);
                }}
                disabled={isImporting}
                activeOpacity={0.7}
              >
                {isImporting ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <>
                    <Ionicons name="download-outline" size={18} color={colors.primary} />
                    <Text style={styles.importButtonText}>Import</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
            {isPageVerified && onSyncPosts && (
              <TouchableOpacity
                style={[styles.actionButton, styles.importButton]}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onSyncPosts(page);
                }}
                disabled={isSyncingPosts}
                activeOpacity={0.7}
              >
                {isSyncingPosts ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <>
                    <Ionicons name="sync-outline" size={18} color={colors.primary} />
                    <Text style={styles.importButtonText}>Sync Posts</Text>
                  </>
                )}
              </TouchableOpacity>
            )}
            {(page.status === 'pending' || feedNotSubscribed) && onRegister && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onRegister(page);
                }}
                disabled={isRegistering}
                activeOpacity={0.7}
              >
                {isRegistering ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : (
                  <Ionicons name="cloud-upload-outline" size={18} color={colors.primary} />
                )}
              </TouchableOpacity>
            )}
            {!page.is_default && isPageVerified && onSetDefault && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onSetDefault(page);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="star-outline" size={18} color={colors.primary} />
              </TouchableOpacity>
            )}
            {onDelete && (
              <TouchableOpacity
                style={styles.actionButton}
                onPress={(e) => {
                  e?.stopPropagation?.();
                  onDelete(page.page_id);
                }}
                activeOpacity={0.7}
              >
                <Ionicons name="trash-outline" size={18} color={colors.error} />
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.md,
    marginBottom: spacing.sm,
    ...(Platform.OS !== 'web' && {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 1 },
      shadowOpacity: 0.1,
      shadowRadius: 2,
      elevation: 2,
    }),
  },
  verifiedCard: {
    borderColor: colors.success,
    backgroundColor: withAlpha(colors.success, 0.03),
  },
  defaultCard: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
  cardContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  pageInfo: {
    flex: 1,
  },
  pageHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  pageIconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.background.secondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  verifiedIconWrapper: {
    backgroundColor: withAlpha(colors.success, 0.13),
  },
  pageDetails: {
    flex: 1,
  },
  pageNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  pageName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  verifiedText: {
    color: colors.success,
  },
  defaultBadge: {
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.full,
    backgroundColor: colors.primary,
  },
  defaultBadgeText: {
    fontSize: typography.fontSize.xs,
    color: colors.white,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    marginBottom: spacing.xs / 2,
  },
  statusText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  verifiedStatusText: {
    color: colors.success,
  },
  pendingStatusText: {
    color: colors.warning,
  },
  verifiedName: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    fontStyle: 'italic',
  },
  pageDetail: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
    marginTop: spacing.xs / 2,
  },
  feedWarningRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: spacing.xs / 2,
  },
  feedWarningText: {
    flex: 1,
    fontSize: typography.fontSize.xs,
    color: colors.warning,
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
  importButton: {
    width: 'auto',
    paddingHorizontal: spacing.sm,
    flexDirection: 'row',
    gap: spacing.xs,
  },
  importButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
});
